import React, { useState, useContext, createContext, useRef, useMemo, useCallback } from "react";
import { OptionsType } from "../../_types/form";
import { CandidateStatusItems, CandidateStatusItemsNonApproved } from "../../config/formData";
import { MeInterface } from "../../_types/user";
import { ICandidateAdditionalCandidateStatusConfig } from "../../_models/data.meCustomerModel";
import { ICandidateStatusWorkflow } from "../../_models/data.candidateStatusWorkflow.model";
import { ICandidateArchiveReason, ICandidateArchiveReasonItem } from "../../_models/data.candidateArchivedReasons.model";
import { useAuth } from "./auth.store";
import { GetCandidateStatusWorkflowConfig, GetCandidateStatusWorkflowConfigOnly, GetCandidateArchivedReasons } from "../../functions/http-functions/candidateConfig";
import { CandidateStatus } from "../../_enums/candidateStatus.enum";

export interface GlobalCandidateStatusesState {
	candidateStatuses: OptionsType[];
	candidateStatusesNonApproved: OptionsType[];
	updateCandidateStatuses: (additionalCandidateStatusConfig?: ICandidateAdditionalCandidateStatusConfig) => void;
	candidateStatusWorkflow: ICandidateStatusWorkflow | null;
	updateCandidateStatusWorkflow: (candidateStatusWorkflow: ICandidateStatusWorkflow) => void;
	resetCandidateStatusWorkflow: () => void;
	statusWorkflowUpdated: boolean;
	getCandidateArchivedStatuses: () => Promise<ICandidateArchiveReasonItem[] | null>;
	isStatusPending: (status: number) => boolean;
}

export const initialCandidateStatusesState: GlobalCandidateStatusesState = {
	candidateStatuses: null,
	candidateStatusesNonApproved: null,
	updateCandidateStatuses: () => {},
	candidateStatusWorkflow: null,
	updateCandidateStatusWorkflow: () => {},
	resetCandidateStatusWorkflow: () => {},
	statusWorkflowUpdated: false,
	getCandidateArchivedStatuses: async () => [],
	isStatusPending: () => false,
};

const CandidateStatusesStoreContext = createContext<GlobalCandidateStatusesState>(initialCandidateStatusesState);

type Props = {
	children: React.ReactChild;
	me: MeInterface;
};

export const CandidateStatusesStoreProvider = ({ children, me }: Props) => {
	const [candidateStatuses, setCandidateStatuses] = useState<OptionsType[]>(CandidateStatusItems);
	const [candidateStatusesNonApproved, setCandidateStatusesNonApproved] = useState<OptionsType[]>(CandidateStatusItemsNonApproved);
	const [candidateStatusWorkflow, setCandidateStatusWorkflow] = useState<ICandidateStatusWorkflow | null>(null);

	const statusUpdated = useRef(null);
	const statusWorkflowUpdated = useRef(null);

	const { auth } = useAuth();

	const updateCandidateStatuses = async (additionalCandidateStatusConfig?: ICandidateAdditionalCandidateStatusConfig) => {
		try {
			if (!statusUpdated?.current) {
				let updatedStatuses = [...CandidateStatusItems];
				let updatedNonApprovedStatuses = [...CandidateStatusItemsNonApproved];
				if (additionalCandidateStatusConfig?.hold || additionalCandidateStatusConfig?.future || additionalCandidateStatusConfig?.dnc || additionalCandidateStatusConfig?.pre) {
					if (additionalCandidateStatusConfig?.hold) {
						const newStatus = {
							ID: additionalCandidateStatusConfig.hold.id,
							Name: additionalCandidateStatusConfig.hold.name,
						};
						updatedStatuses.splice(1, 0, newStatus);
						updatedNonApprovedStatuses.splice(0, 0, newStatus); // Insert at 2nd last position
					}
					if (additionalCandidateStatusConfig?.future) {
						const newStatus = {
							ID: additionalCandidateStatusConfig.future.id,
							Name: additionalCandidateStatusConfig.future.name,
						};
						updatedStatuses.splice(updatedStatuses.length - 1, 0, newStatus);
						updatedNonApprovedStatuses.splice(updatedNonApprovedStatuses.length - 1, 0, newStatus); // Insert at 2nd last position
					}
					if (additionalCandidateStatusConfig?.dnc) {
						const newStatus = {
							ID: additionalCandidateStatusConfig.dnc.id,
							Name: additionalCandidateStatusConfig.dnc.name,
						};
						updatedStatuses.push(newStatus);
					}
					if (additionalCandidateStatusConfig?.pre) {
						const newStatus = {
							ID: additionalCandidateStatusConfig.pre.id,
							Name: additionalCandidateStatusConfig.pre.name,
						};
						updatedStatuses.push(newStatus);
					}
				}

				// get custom candidate statuses
				const customCandidateStatuses = await getCustomCandidateStatuses();

				if (customCandidateStatuses) {
					// default statuses
					const statusMappings = [
						{ key: "Approved", defaultLabel: "Approved", enumValue: CandidateStatus.Approved },
						{ key: "Pending", defaultLabel: "Pending", enumValue: CandidateStatus.Pending },
						{ key: "Rejected", defaultLabel: "Archived", enumValue: CandidateStatus.Rejected },
					];

					try {
						// loop through default statuses and update with custom status labels if they exist and are different
						statusMappings.forEach(({ key, enumValue }) => {
							const customStatus = customCandidateStatuses[key];
							if (customStatus?.label) {
								const status = updatedStatuses.find((status) => status.ID === enumValue);
								if (status) {
									status.Name = customStatus.label;
								}
							}
						});
					} catch (error) {
						console.error("Failed to update candidate statuses with custom configuration:", error);
					}
				}

				setCandidateStatuses(updatedStatuses);
				setCandidateStatusesNonApproved(updatedNonApprovedStatuses);
				statusUpdated.current = true;
			}
		} catch (error) {
			console.error("Failed to update candidate statuses:", error);
		}
	};

	const updateCandidateStatusWorkflow = useCallback((candidateStatusWorkflow: ICandidateStatusWorkflow) => {
		if (!statusWorkflowUpdated?.current) {
			setCandidateStatusWorkflow(candidateStatusWorkflow);
			statusWorkflowUpdated.current = true;
		}
	}, []);

	const resetCandidateStatusWorkflow = useCallback(() => {
		statusUpdated.current = false;
		statusWorkflowUpdated.current = false;
	}, []);

	const getCustomCandidateStatuses = async () => {
		try {
			if (auth?.token) {
				const response = await GetCandidateStatusWorkflowConfigOnly(auth.token);
				return response?.data || null;
			}
			return null;
		} catch (error) {
			console.error("Failed to fetch candidate status workflow:", error);
			return null;
		}
	};

	const getCandidateArchivedStatuses = async (): Promise<ICandidateArchiveReasonItem[] | []> => {
		try {
			if (auth?.token) {
				const response = await GetCandidateArchivedReasons(auth.token);
				return response?.data || [];
			}
			return [];
		} catch (error) {
			console.error("Failed to fetch candidate archived statuses:", error);
			return null;
		}
	};

	const isStatusPending = (status: number) => {
		return status === CandidateStatus.Pending || status === CandidateStatus.Future || status === CandidateStatus.Pre;
	};

	return (
		<CandidateStatusesStoreContext.Provider
			value={{
				candidateStatuses,
				candidateStatusesNonApproved,
				updateCandidateStatuses,
				candidateStatusWorkflow,
				updateCandidateStatusWorkflow,
				resetCandidateStatusWorkflow,
				statusWorkflowUpdated: statusWorkflowUpdated.current,
				getCandidateArchivedStatuses,
				isStatusPending,
			}}>
			{children}
		</CandidateStatusesStoreContext.Provider>
	);
};

export const useCandidateStatus = () => useContext(CandidateStatusesStoreContext);
